import React from "react";
import { useState, useMemo, useEffect } from "react";
import * as XLSX from 'xlsx';

import "./Grids.css";
import {
  FaSortAmountUpAlt,
  FaSortAmountDown,
  FaSort,
  FaPlusCircle,
  FaFileExcel,
  FaTimesCircle,
  FaCheckCircle,
  FaEnvelope,
  FaPhoneAlt,
  FaRegSadCry,
  FaGhost,
  FaExchangeAlt,
  FaExternalLinkAlt
} from "react-icons/fa";

import { DateFormatter } from "../../data/helper";
import {
  Column,
  Table,
  useReactTable,
  ColumnFiltersState,
  getCoreRowModel,
  getFilteredRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFacetedMinMaxValues,
  getPaginationRowModel,
  getSortedRowModel,
  FilterFn,
  ColumnDef,
  flexRender,
} from "@tanstack/react-table";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { DropDownList, ComboBox } from "@progress/kendo-react-dropdowns";

import { RankingInfo, rankItem } from "@tanstack/match-sorter-utils";

import { enquiryGrid, getEnquiryTypes, dropDown } from "../../data/interfaces";
import { Switch } from "@progress/kendo-react-inputs";
import { Input } from "@progress/kendo-react-inputs";
import dayjs from 'dayjs';
import { JobCostingItem, getJobsForDropdown, moveJobCost, addJobCostingItem, AddJobCostingItemDTO, updateJobCostingItem, UpdateJobCostingItemDTO, getJobCostInvoiceFileUrl } from "../../data/jobData";
import { useToast } from "../../contexts/ToastContext";

declare module "@tanstack/table-core" {
  interface FilterFns {
    fuzzy: FilterFn<unknown>;
    numberRange: FilterFn<unknown>;
  }
  interface FilterMeta {
    itemRank: RankingInfo;
  }
}

const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
  // Rank the item
  const itemRank = rankItem(row.getValue(columnId), value);

  // Store the itemRank info
  addMeta({
    itemRank,
  });

  // Return if the item should be filtered in/out
  return itemRank.passed;
};

const numberRangeFilter: FilterFn<any> = (row, columnId, value) => {
  const rowValue = row.getValue(columnId);
  const [min, max] = value as [number | string | undefined, number | string | undefined];
  
  if (rowValue === null || rowValue === undefined) return false;
  
  const numValue = Number(rowValue);
  if (isNaN(numValue)) return false;
  
  const minPassed = min === undefined || min === '' || numValue >= Number(min);
  const maxPassed = max === undefined || max === '' || numValue <= Number(max);
  
  return minPassed && maxPassed;
};

function JobCostingsGrid(props: any) {
  const dateFormatter = new DateFormatter();
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const { showToast } = useToast();

  // Move job cost modal state
  const [moveModalVisible, setMoveModalVisible] = useState(false);
  const [selectedJobCost, setSelectedJobCost] = useState<JobCostingItem | null>(null);
  const [availableJobs, setAvailableJobs] = useState<dropDown[]>([]);
  const [availableJobsOrig, setAvailableJobsOrig] = useState<dropDown[]>([]);
  const [selectedTargetJob, setSelectedTargetJob] = useState<dropDown | null>(null);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [movingCost, setMovingCost] = useState(false);

  // Add job costing item modal state
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [addingItem, setAddingItem] = useState(false);
  const [newJobCostData, setNewJobCostData] = useState<AddJobCostingItemDTO>({
    jobId: props.jobId ? parseInt(props.jobId.toString()) : 0,
    type: '',
    accountRef: '',
    date: new Date().toISOString().split('T')[0],
    reference: '',
    costCode: '',
    details: '',
    debit: 0,
    credit: 0,
    xeroReference: ''
  });

  // Edit job costing item modal state
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState(false);
  const [editJobCostData, setEditJobCostData] = useState<UpdateJobCostingItemDTO | null>(null);

  const exportToExcel = () => {
    let date = new Date().toDateString();
    date = dateFormatter.format(date);

    // Format the data before exporting
    const formattedData = props.onData.map((item: JobCostingItem) => ({
      ...item,
      date: item.date ? dayjs(item.date).format("DD-MM-YYYY") : "",
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(formattedData);
    XLSX.utils.book_append_sheet(wb, ws, "Job Costing");
    XLSX.writeFile(wb, `job_costing_${date}.xlsx`);
  };

  const clientColumns = useMemo<ColumnDef<JobCostingItem>[]>(
    () => [
      {
        accessorKey: "date",
        header: "Date",
        cell: ({ row }: { row: { original: any } }) => (
            <span>
              {row.original.date ? dayjs(row.original.date).format("DD-MM-YYYY") : "Missing date"}
            </span>

        ),  
      },
      {
        accessorKey: "accountRef",
        header: "Account Ref",
      },      
      // {
      //   accessorKey: "auditNumber",
      //   header: "Audit Number",
      // },
      // {
      //   accessorKey: "costCode",
      //   header: "Cost Code",
      // },   
      {
        accessorKey: "rate",
        header: "Rate",
        // enableColumnFilter: false,
      },
      {
        accessorKey: "hoursWorked",
        header: "Hours Worked",
      },         
      {
        accessorKey: "amount",
        header: "Amount",
        // enableColumnFilter: false,

      },
      // {
      //   accessorKey: "jobCostId",
      //   header: "Job Cost ID",
      //   // enableColumnFilter: false,

      // },   
      // {
      //   accessorKey: "jobId",
      //   header: "Job ID",
      //   // enableColumnFilter: false,

      // }, 
      {
        accessorKey: "reference",
        header: "Reference",
      },   
      {
        accessorKey: "type",
        header: "Type",
      },  
      // {
      //   accessorKey: "xeroReference",
      //   header: "Xero Reference",
      // },                         
      {
        accessorKey: "details",
        header: "Details",
        footer: (props) => props.column.id,
        cell: ({ row }: { row: { original: JobCostingItem } }) => (
          <Tooltip anchorElement="target" position="top">
            <span
              title={row.original.details}
              className="overflow__container"
            >
              {row.original.details}
            </span>
          </Tooltip>
        ),
      }, 
      {
        accessorKey: "actions",
        header: "Actions",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: { original: JobCostingItem } }) => (
          <div style={{ display: 'flex', gap: '8px' }}>
            {row.original.accountRef !== "Labour" && (
              <button
                onClick={() => openMoveModal(row.original)}
                className="grid__action__button"
                title="Move to another job"
                style={{
                  backgroundColor: '#21433b',
                  color: 'white',
                  border: 'none',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <FaExchangeAlt />
                Move
              </button>
            )}
            {row.original.xeroReference && (
              <button
                onClick={() => openInvoiceFile(row.original)}
                className="grid__action__button"
                title="View original invoice"
                style={{
                  backgroundColor: '#13b5ea',
                  color: 'white',
                  border: 'none',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <FaExternalLinkAlt />
                Invoice
              </button>
            )}
          </div>
        ),
      }
    ],
    []
  );

  const [data, setData] = useState<enquiryGrid[]>([]);

  useEffect(() => {
    setData(props.onData);
  }, [props.onData]);

  // Update jobId in newJobCostData when props.jobId changes
  useEffect(() => {
    if (props.jobId) {
      const jobIdToUse = parseInt(props.jobId.toString());
      console.log('Props.jobId changed:', props.jobId, 'parsed to:', jobIdToUse);
      setNewJobCostData(prev => ({
        ...prev,
        jobId: jobIdToUse
      }));
    }
  }, [props.jobId]);

  const table = useReactTable({
    data,
    columns: clientColumns,
    filterFns: {
      fuzzy: fuzzyFilter,
      numberRange: numberRangeFilter,
    },
    state: {
      columnFilters,
      globalFilter,
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: fuzzyFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    debugTable: false,
    debugHeaders: false,
    debugColumns: false,
  });

  // show deleted - kept original names for simplicity
  const [showDead, setShowDead] = useState(false);
  const handleShowDead = (event: any) => {
    setShowDead(event.target.value);
    props.onShowDead(event.target.value);
  };

  // show completed - kept original names for simplicity
  const [showProgressed, setShowProgressed] = useState(false);
  const handleShowProgressed = (event: any) => {
    setShowProgressed(event.target.value);
    props.onShowProgressed(event.target.value);
  };

  // Function to load available jobs
  const loadAvailableJobs = async () => {
    setLoadingJobs(true);
    try {
      console.log('Loading available jobs...');
      const jobs = await getJobsForDropdown();
      console.log('Raw jobs from API:', jobs);
      // Filter out the current job
      const filteredJobs = jobs.filter(job => job.value !== parseInt(props.jobId));
      console.log('Filtered jobs:', filteredJobs);
      setAvailableJobs(filteredJobs);
      setAvailableJobsOrig(filteredJobs);
    } catch (error) {
      console.error('Error loading jobs:', error);
      showToast('Failed to load jobs', 'error');
    } finally {
      setLoadingJobs(false);
    }
  };

  // Function to search jobs dropdown
  const searchJobsDropdown = async (searchTerm: string) => {
    const fetchJobData = async (search: string) => {
      try {
        const jobs = await getJobsForDropdown();
        let filteredJobs = jobs.filter(job => 
          job.value !== parseInt(props.jobId) &&
          job.text.toLowerCase().includes(search.toLowerCase())
        );
        
        // Ensure the currently selected job is always included in the list
        if (selectedTargetJob && !filteredJobs.find(job => job.value === selectedTargetJob.value)) {
          // Add the selected job to the beginning of the filtered list
          filteredJobs.unshift(selectedTargetJob);
        }
        
        setAvailableJobs(filteredJobs);
      } catch (error) {
        console.error('Error searching jobs:', error);
      }
    };
    
    fetchJobData(searchTerm);
  };

  // Function to open move modal
  const openMoveModal = (jobCost: JobCostingItem) => {
    setSelectedJobCost(jobCost);
    setSelectedTargetJob(null);
    setMoveModalVisible(true);
    loadAvailableJobs();
  };

  // Function to open the original invoice file in a new tab
  const openInvoiceFile = async (jobCost: JobCostingItem) => {
    try {
      const url = await getJobCostInvoiceFileUrl(jobCost.jobCostId);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      showToast('Could not retrieve invoice file', 'error');
    }
  };

  // Function to close move modal
  const closeMoveModal = () => {
    setMoveModalVisible(false);
    setSelectedJobCost(null);
    setSelectedTargetJob(null);
    setAvailableJobs([]);
    setAvailableJobsOrig([]);
  };

  // Function to handle the move action
  const handleMoveJobCost = async () => {
    if (!selectedJobCost || !selectedTargetJob) {
      showToast('Please select a target job', 'error');
      return;
    }

    setMovingCost(true);
    try {
      await moveJobCost(selectedJobCost.jobCostId, selectedTargetJob.value);
      showToast(`Job cost moved successfully to ${selectedTargetJob.text}`, 'success');
      closeMoveModal();
      
      // Refresh the grid data
      if (props.onRefresh) {
        props.onRefresh();
      }
    } catch (error) {
      console.error('Error moving job cost:', error);
      showToast('Failed to move job cost', 'error');
    } finally {
      setMovingCost(false);
    }
  };

  // Functions for adding job costing items
  const openAddModal = () => {
    console.log('Opening add modal - props.jobId:', props.jobId, 'type:', typeof props.jobId);
    console.log('Props.jobRef (formatted):', props.jobRef);
    const jobIdToUse = props.jobId ? parseInt(props.jobId.toString()) : 0;
    console.log('Parsed jobId for API:', jobIdToUse);
    
    setNewJobCostData({
      jobId: jobIdToUse,
      type: '',
      accountRef: '',
      date: new Date().toISOString().split('T')[0],
      reference: '',
      costCode: '',
      details: '',
      debit: 0,
      credit: 0,
      xeroReference: ''
    });
    setAddModalVisible(true);
  };

  const closeAddModal = () => {
    setAddModalVisible(false);
    const jobIdToUse = props.jobId ? parseInt(props.jobId.toString()) : 0;
    setNewJobCostData({
      jobId: jobIdToUse,
      type: '',
      accountRef: '',
      date: new Date().toISOString().split('T')[0],
      reference: '',
      costCode: '',
      details: '',
      debit: 0,
      credit: 0,
      xeroReference: ''
    });
  };

  const handleAddJobCostItem = async () => {
    if (!newJobCostData.reference || !newJobCostData.details) {
      showToast('Please fill in all required fields (Account Reference, Reference, Details)', 'error');
      return;
    }

    console.log('Props jobId:', props.jobId, 'type:', typeof props.jobId);
    console.log('Adding new job costing item:', newJobCostData);
    console.log('Job ID in data:', newJobCostData.jobId, 'type:', typeof newJobCostData.jobId);

    setAddingItem(true);
    try {
      await addJobCostingItem(newJobCostData);
      showToast('Job costing item added successfully', 'success');
      closeAddModal();
      
      // Refresh the grid data
      if (props.onRefresh) {
        props.onRefresh();
      }
    } catch (error) {
      console.error('Error adding job costing item:', error);
      showToast('Failed to add job costing item', 'error');
    } finally {
      setAddingItem(false);
    }
  };

  const handleInputChange = (field: keyof AddJobCostingItemDTO, value: any) => {
    setNewJobCostData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Functions for editing job costing items
  const openEditModal = (jobCost: JobCostingItem) => {
    console.log('Opening edit modal for job cost:', jobCost);
    
    // Determine debit/credit from either the separate fields or the amount field
    let debitValue = 0;
    let creditValue = 0;
    
    // Check if we have explicit debit/credit values
    if (jobCost.debit && jobCost.debit !== 0) {
      debitValue = jobCost.debit;
    } else if (jobCost.credit && jobCost.credit !== 0) {
      creditValue = jobCost.credit;
    } else if (jobCost.amount !== undefined && jobCost.amount !== null) {
      // Fallback to amount field
      if (jobCost.amount >= 0) {
        debitValue = jobCost.amount;
      } else {
        creditValue = Math.abs(jobCost.amount);
      }
    }
    
    setEditJobCostData({
      jobCostId: jobCost.jobCostId,
      jobId: jobCost.jobId,
      type: jobCost.type || '',
      accountRef: jobCost.accountRef,
      date: jobCost.date ? jobCost.date.split('T')[0] : new Date().toISOString().split('T')[0],
      reference: jobCost.reference,
      costCode: jobCost.costCode || '',
      details: jobCost.details,
      debit: debitValue,
      credit: creditValue,
      xeroReference: jobCost.xeroReference || ''
    });
    setEditModalVisible(true);
  };

  const closeEditModal = () => {
    setEditModalVisible(false);
    setEditJobCostData(null);
  };

  const handleEditJobCostItem = async () => {
    if (!editJobCostData) {
      return;
    }

    if (!editJobCostData.reference || !editJobCostData.details) {
      showToast('Please fill in all required fields (Reference, Details)', 'error');
      return;
    }

    console.log('Updating job costing item:', editJobCostData);

    setEditingItem(true);
    try {
      await updateJobCostingItem(editJobCostData);
      showToast('Job costing item updated successfully', 'success');
      closeEditModal();
      
      // Refresh the grid data
      if (props.onRefresh) {
        props.onRefresh();
      }
    } catch (error) {
      console.error('Error updating job costing item:', error);
      showToast('Failed to update job costing item', 'error');
    } finally {
      setEditingItem(false);
    }
  };

  const handleEditInputChange = (field: keyof UpdateJobCostingItemDTO, value: any) => {
    if (!editJobCostData) return;
    setEditJobCostData(prev => prev ? ({
      ...prev,
      [field]: value
    }) : null);
  };

  return (
    <div className="p-2">
      {/* This is the global column search box */}
      <div className="grid__title__container">
        <div className="grid__header__background">
          <div className="grid__title">
            <div style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                alignItems: "center",
              }}>
            <div>Job Costing - {props.jobRef || props.jobId}</div>
            </div>            
            <div style={{display: "flex", flexDirection: "row"}}>
              <div onClick={openAddModal} className="grid__export__button">
                <FaPlusCircle
                  style={{ fontSize: "14pt", marginRight: "1rem" }}
                />{" "}
                <p style={{ margin: 0 }}>Add Job Costing</p>
              </div>
              <div onClick={exportToExcel} className="grid__export__button">
                <FaFileExcel
                  style={{ fontSize: "14pt", marginRight: "1rem" }}
                />{" "}
                <p style={{ margin: 0 }}>Export job costing</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="h-2" />
      <table className="grid__table">
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                return (
                  <th key={header.id} colSpan={header.colSpan}>
                    {header.isPlaceholder ? null : (
                      <>
                        {/* This DIV is where you hover/click on the column header to sort */}
                        <div
                          {...{
                            className: header.column.getCanSort()
                              ? "header__sortable"
                              : "",
                            onClick: header.column.getToggleSortingHandler(),
                          }}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {{
                            asc: (
                              <FaSortAmountUpAlt style={{ fontSize: "14pt" }} />
                            ),
                            desc: (
                              <FaSortAmountDown style={{ fontSize: "14pt" }} />
                            ),
                          }[header.column.getIsSorted() as string] ?? (
                            <FaSort style={{ fontSize: "14pt" }} />
                          )}
                        </div>

                        {header.column.getCanFilter() ? (
                          <div>
                            <Filter column={header.column} table={table} />
                          </div>
                        ) : null}
                      </>
                    )}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => {
            const bgColor = () => {
              if (row.original.deadDate) {
                return "rgba(235, 199, 199, 0.5)";
              } else if (row.original.progressed) {
                return "rgba(215, 237, 215, 1)";
              } else {
                return "rgba(245, 249, 251, 1)";
              }
            };

            return (
              <tr
                onDoubleClick={() => openEditModal(row.original)}
                style={{ backgroundColor: bgColor(), cursor: 'pointer' }}
                key={row.id}
              >
                {row.getVisibleCells().map((cell) => {
                  return (
                    <td key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* This is the pagination controls */}
      <div className="page__container">
        <div className="page__button__container">
          <button
            className="page__button"
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
          >
            {"<<"}
          </button>
          <button
            className="page__button"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            {"<"}
          </button>
          <button
            className="page__button"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            {">"}
          </button>
          <button
            className="page__button"
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
            disabled={!table.getCanNextPage()}
          >
            {">>"}
          </button>
        </div>

        <div className="page__controls">
          <div className="page__numbers">
            <div>Page&nbsp;</div>
            <strong>
              {table.getState().pagination.pageIndex + 1} of{" "}
              {table.getPageCount()}
            </strong>
          </div>
          <div className="page__numbers__goto">
            <div className="page__numbers__gototext">| Go to page:</div>
            <input
              type="number"
              defaultValue={table.getState().pagination.pageIndex + 1}
              onChange={(e) => {
                const page = e.target.value ? Number(e.target.value) - 1 : 0;
                table.setPageIndex(page);
              }}
              className="filter__input"
            />
          </div>
          <select
            value={table.getState().pagination.pageSize}
            onChange={(e) => {
              table.setPageSize(Number(e.target.value));
            }}
            className="page__select"
          >
            {[10, 20, 30, 40, 50].map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                Show {pageSize}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Move Job Cost Modal */}
      {moveModalVisible && (
        <Dialog
          title="Move Job Cost to Another Job"
          onClose={closeMoveModal}
          width="500px"
        >
          <div style={{ padding: '16px' }}>
            <p>Move the following job cost to another job:</p>
            <div style={{ 
              backgroundColor: '#f5f5f5', 
              padding: '12px', 
              borderRadius: '4px',
              marginBottom: '16px'
            }}>
              <p style={{ margin: '0 0 8px 0' }}>
                <strong>Account Ref:</strong> {selectedJobCost?.accountRef}
              </p>
              <p style={{ margin: '0 0 8px 0' }}>
                <strong>Amount:</strong> £{selectedJobCost?.amount?.toFixed(2)}
              </p>
              <p style={{ margin: '0' }}>
                <strong>Date:</strong> {selectedJobCost && selectedJobCost.date ? dayjs(selectedJobCost.date).format("DD-MM-YYYY") : "N/A"}
              </p>
            </div>
            
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                Select Target Job:
              </label>
              {/* Debug info */}
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                Available jobs: {availableJobs.length}, Loading: {loadingJobs.toString()}
                <br />
                Selected job: {selectedTargetJob ? `${selectedTargetJob.text} (${selectedTargetJob.value})` : 'None'}
              </div>
              <ComboBox
                data={availableJobs}
                textField="text"
                dataItemKey="value"
                value={selectedTargetJob}
                filterable={true}
                onFilterChange={(e) => {
                  if (e.filter.value) {
                    searchJobsDropdown(e.filter.value);
                  } else {
                    // When filter is cleared, ensure the selected job remains in the list
                    let jobsToShow = [...availableJobsOrig];
                    if (selectedTargetJob && !jobsToShow.find(job => job.value === selectedTargetJob.value)) {
                      jobsToShow.unshift(selectedTargetJob);
                    }
                    setAvailableJobs(jobsToShow);
                  }
                }}
                onChange={(e) => {
                  console.log('ComboBox onChange event:', e);
                  console.log('e.value:', e.value);
                  console.log('e.target:', e.target);
                  console.log('Available jobs:', availableJobs);
                  
                  // For ComboBox, e.value should be the complete object
                  setSelectedTargetJob(e.value || null);
                }}
                onBlur={(e) => {
                  // Preserve the selected value when ComboBox loses focus
                  console.log('ComboBox onBlur - selectedTargetJob:', selectedTargetJob);
                }}
                loading={loadingJobs}
                style={{ width: '100%' }}
                placeholder="Search for a job..."
              />
            </div>
          </div>
          
          <DialogActionsBar>
            <button
              className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
              onClick={closeMoveModal}
              disabled={movingCost}
            >
              Cancel
            </button>
            <button
              className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary"
              onMouseDown={(e) => {
                // Prevent the ComboBox from losing focus when button is clicked
                e.preventDefault();
              }}
              onClick={handleMoveJobCost}
              disabled={!selectedTargetJob || movingCost}
            >
              {movingCost ? 'Moving...' : 'Move Job Cost'}
            </button>
          </DialogActionsBar>
        </Dialog>
      )}

      {/* Add Job Costing Item Modal */}
      {addModalVisible && (
        <Dialog
          title="Add Job Costing Item"
          onClose={closeAddModal}
          width="500px"
        >
          <div style={{ padding: '16px' }}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                Account Ref:
              </label>
              <Input
                value={newJobCostData.accountRef}
                onChange={(e) => handleInputChange('accountRef', e.target.value)}
                style={{ width: '100%' }}
              />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                Reference:
              </label>
              <Input
                value={newJobCostData.reference}
                onChange={(e) => handleInputChange('reference', e.target.value)}
                style={{ width: '100%' }}
              />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                Details:
              </label>
              <Input
                value={newJobCostData.details}
                onChange={(e) => handleInputChange('details', e.target.value)}
                style={{ width: '100%' }}
              />
            </div>
            <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                  Amount (£):
                </label>
                <Input
                  type="number"
                  step="0.01"
                  value={
                    newJobCostData.debit !== 0 
                      ? newJobCostData.debit.toString() 
                      : (newJobCostData.credit && newJobCostData.credit !== 0) 
                        ? (-(newJobCostData.credit)).toString() 
                        : ''
                  }
                  onChange={(e) => {
                    const value = e.target.value as string;
                    
                    // Handle empty string or just a minus sign
                    if (value === '' || value === '-') {
                      setNewJobCostData(prev => ({
                        ...prev,
                        debit: 0,
                        credit: 0
                      }));
                      return;
                    }
                    
                    // Parse the number
                    const numValue = parseFloat(value);
                    if (!isNaN(numValue)) {
                      if (numValue >= 0) {
                        // Positive number goes to debit
                        setNewJobCostData(prev => ({
                          ...prev,
                          debit: numValue,
                          credit: 0
                        }));
                      } else {
                        // Negative number goes to credit (as absolute value)
                        setNewJobCostData(prev => ({
                          ...prev,
                          debit: 0,
                          credit: Math.abs(numValue)
                        }));
                      }
                    }
                  }}
                  style={{ width: '100%' }}
                  placeholder="Enter amount (positive or negative)"
                />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                Date:
              </label>
              <Input
                type="date"
                value={newJobCostData.date.split('T')[0]}
                onChange={(e) => handleInputChange('date', e.target.value)}
                style={{ width: '100%' }}
              />
            </div>
          </div>
          
          <DialogActionsBar>
            <button
              className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
              onClick={closeAddModal}
              disabled={addingItem}
            >
              Cancel
            </button>
            <button
              className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary"
              onClick={handleAddJobCostItem}
              disabled={addingItem}
            >
              {addingItem ? 'Adding...' : 'Add Job Costing Item'}
            </button>
          </DialogActionsBar>
        </Dialog>
      )}

      {/* Edit Job Costing Item Modal */}
      {editModalVisible && editJobCostData && (
        <Dialog
          title="Edit Job Costing Item"
          onClose={closeEditModal}
          width="500px"
        >
          <div style={{ padding: '16px' }}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                Account Ref:
              </label>
              <Input
                value={editJobCostData.accountRef}
                onChange={(e) => handleEditInputChange('accountRef', e.target.value)}
                style={{ width: '100%' }}
              />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                Reference:
              </label>
              <Input
                value={editJobCostData.reference}
                onChange={(e) => handleEditInputChange('reference', e.target.value)}
                style={{ width: '100%' }}
              />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                Details:
              </label>
              <Input
                value={editJobCostData.details}
                onChange={(e) => handleEditInputChange('details', e.target.value)}
                style={{ width: '100%' }}
              />
            </div>
            <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                  Amount (£):
                </label>
                <Input
                  type="number"
                  step="0.01"
                  value={
                    editJobCostData.debit !== 0 
                      ? editJobCostData.debit.toString() 
                      : (editJobCostData.credit && editJobCostData.credit !== 0) 
                        ? (-(editJobCostData.credit)).toString() 
                        : ''
                  }
                  onChange={(e) => {
                    const value = e.target.value as string;
                    
                    // Handle empty string or just a minus sign
                    if (value === '' || value === '-') {
                      setEditJobCostData(prev => prev ? ({
                        ...prev,
                        debit: 0,
                        credit: 0
                      }) : null);
                      return;
                    }
                    
                    // Parse the number
                    const numValue = parseFloat(value);
                    if (!isNaN(numValue)) {
                      if (numValue >= 0) {
                        // Positive number goes to debit
                        setEditJobCostData(prev => prev ? ({
                          ...prev,
                          debit: numValue,
                          credit: 0
                        }) : null);
                      } else {
                        // Negative number goes to credit (as absolute value)
                        setEditJobCostData(prev => prev ? ({
                          ...prev,
                          debit: 0,
                          credit: Math.abs(numValue)
                        }) : null);
                      }
                    }
                  }}
                  style={{ width: '100%' }}
                  placeholder="Enter amount (positive or negative)"
                />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                Date:
              </label>
              <Input
                type="date"
                value={editJobCostData.date.split('T')[0]}
                onChange={(e) => handleEditInputChange('date', e.target.value)}
                style={{ width: '100%' }}
              />
            </div>
          </div>
          
          <DialogActionsBar>
            <button
              className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
              onClick={closeEditModal}
              disabled={editingItem}
            >
              Cancel
            </button>
            <button
              className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary"
              onClick={handleEditJobCostItem}
              disabled={editingItem}
            >
              {editingItem ? 'Updating...' : 'Update Job Costing Item'}
            </button>
          </DialogActionsBar>
        </Dialog>
      )}
    </div>
  );
}

function Filter({
  column,
  table,
}: {
  column: Column<any, unknown>;
  table: Table<any>;
}) {
  const firstValue = table
    .getPreFilteredRowModel()
    .flatRows[0]?.getValue(column.id);

  const columnFilterValue = column.getFilterValue();

  const sortedUniqueValues = useMemo(
    () =>
      typeof firstValue === "number"
        ? []
        : Array.from(column.getFacetedUniqueValues().keys()).sort(),
    [column.getFacetedUniqueValues()]
  );

  return typeof firstValue === "number" ? (
    <div>
      <div className="flex space-x-2">
        <DebouncedInput
          type="number"
          min={Number(column.getFacetedMinMaxValues()?.[0] ?? "")}
          max={Number(column.getFacetedMinMaxValues()?.[1] ?? "")}
          value={(columnFilterValue as [number, number])?.[0] ?? ""}
          onChange={(value) =>
            column.setFilterValue((old: [number, number]) => [value, old?.[1]])
          }
          placeholder={`Min ${
            column.getFacetedMinMaxValues()?.[0]
              ? `(${column.getFacetedMinMaxValues()?.[0]})`
              : ""
          }`}
          className="filter__input__minmax"
        />
        <DebouncedInput
          type="number"
          min={Number(column.getFacetedMinMaxValues()?.[0] ?? "")}
          max={Number(column.getFacetedMinMaxValues()?.[1] ?? "")}
          value={(columnFilterValue as [number, number])?.[1] ?? ""}
          onChange={(value) =>
            column.setFilterValue((old: [number, number]) => [old?.[0], value])
          }
          placeholder={`Max ${
            column.getFacetedMinMaxValues()?.[1]
              ? `(${column.getFacetedMinMaxValues()?.[1]})`
              : ""
          }`}
          className="filter__input__minmax"
        />
      </div>
      <div className="h-1" />
    </div>
  ) : (
    <>
      <datalist id={column.id + "list"}>
        {sortedUniqueValues.slice(0, 5000).map((value: any) => (
          <option value={value} key={value} />
        ))}
      </datalist>
      <DebouncedInput
        type="text"
        value={(columnFilterValue ?? "") as string}
        onChange={(value) => column.setFilterValue(value)}
        placeholder={`Search... (${column.getFacetedUniqueValues().size})`}
        className="filter__input"
        list={column.id + "list"}
      />
    </>
  );
}

// A debounced input react component
function DebouncedInput({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}: {
  value: string | number;
  onChange: (value: string | number) => void;
  debounce?: number;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange">) {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);

    return () => clearTimeout(timeout);
  }, [value]);

  return (
    <input
      {...props}
      value={value}
      onChange={(e) => setValue(e.target.value)}
    />
  );
}

export default JobCostingsGrid;
